// FILE: QaAutomationSection.tsx
// Purpose: Settings → Proyectos → Automatización QA: whether a comment or a
//          label on a GitHub pull request may start work in this project, as
//          which account, and with which agent.
// Layer: Web settings presentation
// Exports: QaAutomationSection

import type { EnvironmentId, ManagedAgentId, ProjectId, UserId } from "@taskin-code/contracts";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import { filterQaCapableAgents } from "@taskin-code/shared/managedAgentQaCapability";

import { useAuthStore } from "../../auth/authStore";
import { type AdminEnvironmentRow, EnvironmentsClient } from "../../auth/EnvironmentsClient";
import { useVisibleManagedAgents } from "../../hooks/useProviderModelCatalog";
import { adminFetch } from "../../lib/adminFetch";
import { environmentQueryKeys } from "../../lib/environmentQueries";
import { SystemSettingsClient } from "../../lib/systemSettingsClient";
import { Button } from "../ui/button";
import { Select, SelectItem, SelectPopup, SelectTrigger, SelectValue } from "../ui/select";
import { Switch } from "../ui/switch";

interface AdminUserRow {
  readonly userId: string;
  readonly email: string;
  readonly name: string;
}

const QA_AUTOMATION_QUERY_KEY = ["system-settings", "qa-automation"] as const;

/**
 * The permissions the actor account needs, spelled out on the screen.
 *
 * Written here rather than left to the documentation because of what this
 * screen does: an admin who has not read `docs/agente-qa.md` is exactly the
 * person about to point the automation at their own account, which is the one
 * choice this feature must not make easy. The server refuses a configuration
 * that lacks any of these and names the missing one, but a refusal after the
 * fact is a worse way to learn it than a line above the picker.
 */
const ACTOR_PERMISSIONS = ["p.thread.create", "p.thread.use", "p.env.use", "p.pr.preview"];

/**
 * The one grant this feature asks for conditionally, mirroring
 * `qaAutomationActorPermissionsFor` on the server.
 *
 * Shown as part of the list the moment the switch below is on, and not only
 * after the server refuses: an admin who turns on «sincronizar al mergear»
 * without it gets a 422 naming a permission they never knew was involved.
 */
const SYNC_PERMISSION = "p.env.sync";

const formatDateTime = (value: string): string => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
};

export function QaAutomationSection(props: { readonly projectId: ProjectId }) {
  const { projectId } = props;
  const canEdit = useAuthStore(
    (state) =>
      state.globalPermissions.has("a.edit") && state.globalPermissions.has("u.assign_permissions"),
  );
  const isUserAdmin = useAuthStore((state) => state.globalPermissions.has("u.view"));

  const settingQuery = useQuery({
    queryKey: QA_AUTOMATION_QUERY_KEY,
    queryFn: () => SystemSettingsClient.readQaAutomation(),
    retry: false,
  });

  // The admin route when the viewer administers accounts, the user route
  // otherwise: whoever hands out permissions may well not hold `p.env.use` on
  // this project, and the picker has to work for both.
  const environmentsQuery = useQuery({
    queryKey: [
      ...environmentQueryKeys.list(projectId, isUserAdmin ? "admin" : "usable"),
      "qa-automation",
    ],
    queryFn: async (): Promise<ReadonlyArray<AdminEnvironmentRow>> =>
      isUserAdmin
        ? EnvironmentsClient.listByProject(projectId)
        : EnvironmentsClient.listUsable(projectId),
    retry: false,
  });

  const usersQuery = useQuery({
    queryKey: environmentQueryKeys.roster(),
    queryFn: async (): Promise<ReadonlyArray<AdminUserRow>> =>
      (await adminFetch<{ users: ReadonlyArray<AdminUserRow> }>("/api/admin/users")).users,
    retry: false,
  });

  const agents = useVisibleManagedAgents();
  // Both operations, not just the first: an agent that can build a preview and
  // not destroy one would fail the reaction to every closed pull request.
  const capableAgents = useMemo(() => {
    const forCreate = filterQaCapableAgents(agents, "preview-create");
    return filterQaCapableAgents(forCreate, "preview-destroy");
  }, [agents]);

  const stored = settingQuery.data?.projects[projectId] ?? null;

  const [enabled, setEnabled] = useState(false);
  const [agentId, setAgentId] = useState<string>("");
  const [actorUserId, setActorUserId] = useState<string>("");
  const [environmentId, setEnvironmentId] = useState<string>("");
  const [syncOnMerge, setSyncOnMerge] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Seeded from the server's answer, and re-seeded when the project changes.
  // Never merged with the local draft: a half-typed configuration overwritten
  // by a refetch is worse than losing it.
  useEffect(() => {
    setEnabled(stored?.enabled ?? false);
    setAgentId(stored?.agentId ?? "");
    setActorUserId(stored?.actorUserId ?? "");
    setEnvironmentId(stored?.environmentId ?? "");
    setSyncOnMerge(stored?.syncOnMerge ?? false);
    setError(null);
    // `saved` is deliberately not reset here: this effect runs again when the
    // save's own refetch lands, which would wipe the confirmation the save had
    // just set. The panel is keyed on the project in `ProjectsSection`, so it
    // remounts rather than carrying a stale "Guardado." to another project.
  }, [stored, projectId]);

  const projectEnvironments = useMemo(
    () =>
      (environmentsQuery.data ?? []).filter(
        (row) => row.kind === "project" && row.status === "ready",
      ),
    [environmentsQuery.data],
  );

  const complete = agentId !== "" && actorUserId !== "" && environmentId !== "";
  const dirty =
    enabled !== (stored?.enabled ?? false) ||
    agentId !== (stored?.agentId ?? "") ||
    actorUserId !== (stored?.actorUserId ?? "") ||
    environmentId !== (stored?.environmentId ?? "") ||
    syncOnMerge !== (stored?.syncOnMerge ?? false);

  const save = async () => {
    setIsSaving(true);
    setError(null);
    setSaved(false);
    try {
      await SystemSettingsClient.writeQaAutomation({
        projectId,
        enabled,
        agentId: agentId as ManagedAgentId,
        actorUserId: actorUserId as UserId,
        environmentId: environmentId as EnvironmentId,
        syncOnMerge,
      });
      await settingQuery.refetch();
      setSaved(true);
    } catch (cause: unknown) {
      // The server's own words: a 422 names the permission or the tool that is
      // missing, which is the only thing that tells an admin what to go and do.
      setError(cause instanceof Error ? cause.message : "No se pudo guardar la automatización.");
    } finally {
      setIsSaving(false);
    }
  };

  if (settingQuery.isPending) {
    return (
      <p className="rounded-xl border border-border/70 px-4 py-3 text-xs text-muted-foreground">
        Cargando la automatización…
      </p>
    );
  }

  if (settingQuery.error !== null) {
    return (
      <p className="rounded-xl border border-border/70 px-4 py-3 text-xs text-muted-foreground">
        {settingQuery.error instanceof Error
          ? settingQuery.error.message
          : "No se pudo cargar la automatización."}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="space-y-3 rounded-xl border border-border/70 px-4 py-3">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground">Automatización desde GitHub</p>
            <p className="text-xs text-muted-foreground">
              Con esto activado, la etiqueta <code className="mx-0.5">preview</code> o un comentario{" "}
              <code className="mx-0.5">/preview</code> en un PR de este repositorio abren un hilo
              con el agente de QA para montar la preview aislada. Un push a un PR que ya tiene
              preview la refresca sin abrir hilo, y cerrar o mergear el PR la destruye.
            </p>
            <p className="mt-1.5 text-xs text-muted-foreground">
              Apagado de fábrica. Quien comenta en GitHub puede no tener cuenta aquí: su login viaja
              como texto en el primer mensaje del hilo y <strong>no</strong> se resuelve a ninguna
              persona.
            </p>
          </div>
          <Switch
            checked={enabled}
            disabled={!canEdit || isSaving || (!enabled && !complete)}
            onCheckedChange={(checked) => {
              setEnabled(Boolean(checked));
              setSaved(false);
            }}
            aria-label="Automatización desde GitHub"
          />
        </div>

        {stored?.disabledReason !== null && stored?.disabledReason !== undefined ? (
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 px-3 py-2">
            <p className="text-xs font-medium text-amber-400">
              El servidor la desactivó solo
              {stored.disabledAt === null ? "" : ` el ${formatDateTime(stored.disabledAt)}`}
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">{stored.disabledReason}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Se apaga sola cuando lo que señala deja de valer —la cuenta actora se desactiva o
              pierde un permiso, al agente le quitan una tool, el entorno desaparece— porque actuar
              sin esos permisos sería una escalada y saltarse el ciclo en silencio te dejaría
              creyendo que funciona. Arregla lo de arriba y vuelve a activarla: la configuración se
              conserva.
            </p>
          </div>
        ) : null}
      </div>

      <div className="space-y-4 rounded-xl border border-border/70 px-4 py-3">
        <div>
          <p className="text-sm font-medium text-foreground">Cuenta actora</p>
          <p className="text-xs text-muted-foreground">
            <strong>
              Activar la automatización es delegarle a esta cuenta lo que la gente pida desde
              GitHub.
            </strong>{" "}
            El hilo que se abre es suyo, y las tools de QA deciden lo que pueden hacer a partir del
            dueño del hilo: el trabajo corre con los permisos de esta cuenta, no con los tuyos.
          </p>
          <p className="mt-1.5 text-xs text-muted-foreground">
            Necesita en este proyecto:{" "}
            {(syncOnMerge ? [...ACTOR_PERMISSIONS, SYNC_PERMISSION] : ACTOR_PERMISSIONS).map(
              (permission, index) => (
                <span key={permission}>
                  {index === 0 ? "" : ", "}
                  <code className="mx-0.5">{permission}</code>
                </span>
              ),
            )}
            . Y no debería tener nada más —{" "}
            {syncOnMerge ? null : (
              <>
                ni <code className="mx-0.5">p.env.sync</code>,{" "}
              </>
            )}
            ni <code className="mx-0.5">p.env.manage</code>, ni permisos de administración: lo que
            le sobre es lo que cualquiera con permiso de comentar en el repositorio podría llegar a
            mover.
          </p>
          <div className="mt-2">
            <Select
              value={actorUserId}
              onValueChange={(value) => {
                if (!value) return;
                setActorUserId(value);
                setSaved(false);
              }}
            >
              <SelectTrigger
                className="w-full sm:w-96"
                aria-label="Cuenta actora"
                disabled={!canEdit}
              >
                <SelectValue>
                  {(usersQuery.data ?? []).find((user) => user.userId === actorUserId)?.name ??
                    (actorUserId === "" ? "Elige una cuenta" : actorUserId)}
                </SelectValue>
              </SelectTrigger>
              <SelectPopup align="start" alignItemWithTrigger={false}>
                {(usersQuery.data ?? []).map((user) => (
                  <SelectItem hideIndicator key={user.userId} value={user.userId}>
                    {user.name} · {user.email}
                  </SelectItem>
                ))}
              </SelectPopup>
            </Select>
            {usersQuery.error !== null ? (
              <p className="mt-1 text-xs text-destructive-foreground">
                No se pudo cargar la lista de cuentas:{" "}
                {usersQuery.error instanceof Error ? usersQuery.error.message : "error"}
              </p>
            ) : null}
          </div>
        </div>

        <div>
          <p className="text-sm font-medium text-foreground">Agente</p>
          <p className="text-xs text-muted-foreground">
            Sólo aparecen los agentes con las tools de QA que hacen falta para montar y retirar una
            preview. Se comprueba otra vez en cada ciclo: si le quitan una, la automatización se
            apaga sola.
          </p>
          <div className="mt-2">
            <Select
              value={agentId}
              onValueChange={(value) => {
                if (!value) return;
                setAgentId(value);
                setSaved(false);
              }}
            >
              <SelectTrigger className="w-full sm:w-96" aria-label="Agente" disabled={!canEdit}>
                <SelectValue>
                  {capableAgents.find((agent) => agent.id === agentId)?.name ??
                    (agentId === "" ? "Elige un agente" : agentId)}
                </SelectValue>
              </SelectTrigger>
              <SelectPopup align="start" alignItemWithTrigger={false}>
                {capableAgents.map((agent) => (
                  <SelectItem hideIndicator key={agent.id} value={agent.id}>
                    {agent.name} · {agent.provider}/{agent.model}
                  </SelectItem>
                ))}
              </SelectPopup>
            </Select>
            {capableAgents.length === 0 ? (
              <p className="mt-1 text-xs text-muted-foreground">
                Ningún agente tiene las tools de QA. Configúralo en Ajustes → Agents.
              </p>
            ) : null}
          </div>
        </div>

        <div>
          <p className="text-sm font-medium text-foreground">Entorno</p>
          <p className="text-xs text-muted-foreground">
            El entorno del que sale cada preview: su código, sus datos y su configuración se copian
            desde aquí, y el hilo de la automatización trabaja dentro. El entorno compartido no se
            toca.
          </p>
          <div className="mt-2">
            <Select
              value={environmentId}
              onValueChange={(value) => {
                if (!value) return;
                setEnvironmentId(value);
                setSaved(false);
              }}
            >
              <SelectTrigger className="w-full sm:w-96" aria-label="Entorno" disabled={!canEdit}>
                <SelectValue>
                  {projectEnvironments.find((row) => row.id === environmentId)?.name ??
                    (environmentId === "" ? "Elige un entorno" : environmentId)}
                </SelectValue>
              </SelectTrigger>
              <SelectPopup align="start" alignItemWithTrigger={false}>
                {projectEnvironments.map((row) => (
                  <SelectItem hideIndicator key={row.id} value={row.id}>
                    {row.name ?? row.id}
                    {row.trackedBranch === null ? "" : ` · ${row.trackedBranch}`}
                  </SelectItem>
                ))}
              </SelectPopup>
            </Select>
            {projectEnvironments.length === 0 ? (
              <p className="mt-1 text-xs text-muted-foreground">
                Este proyecto no tiene ningún entorno de proyecto listo. Créalo en la pestaña
                Entornos.
              </p>
            ) : null}
          </div>
        </div>

        <div className="flex items-start justify-between gap-4 border-t border-border/70 pt-4">
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground">Sincronizar al mergear</p>
            <p className="text-xs text-muted-foreground">
              Cuando un PR se mergea a la rama base del proyecto, el entorno compartido se queda por
              detrás. Con esto activado la automatización abre un hilo que lo actualiza —{" "}
              <strong>uno por ciclo</strong>, aunque hayan entrado varios PRs.
            </p>
            <p className="mt-1.5 text-xs text-muted-foreground">
              Aparte del resto porque no es lo mismo: una preview está aislada y caduca sola,
              mientras que esto mueve el entorno en el que trabaja todo el mundo. Añade{" "}
              <code className="mx-0.5">p.env.sync</code> a lo que necesita la cuenta actora, y si se
              lo quitan la automatización se apaga sola con motivo.
            </p>
          </div>
          <Switch
            checked={syncOnMerge}
            disabled={!canEdit || isSaving}
            onCheckedChange={(checked) => {
              setSyncOnMerge(Boolean(checked));
              setSaved(false);
            }}
            aria-label="Sincronizar al mergear"
          />
        </div>

        {error !== null ? <p className="text-xs text-destructive-foreground">{error}</p> : null}
        {saved && error === null ? (
          <p className="text-xs text-muted-foreground">Guardado.</p>
        ) : null}

        <div className="flex items-center gap-3">
          <Button
            type="button"
            disabled={!canEdit || isSaving || !dirty || (enabled && !complete)}
            onClick={() => {
              void save();
            }}
          >
            {isSaving ? "Guardando…" : "Guardar"}
          </Button>
          {!canEdit ? (
            <p className="text-xs text-muted-foreground">
              Para cambiar esto hacen falta <code className="mx-0.5">a.edit</code> y{" "}
              <code className="mx-0.5">u.assign_permissions</code>: repartir autoridad es lo que
              gobierna el segundo.
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
